'use client';

import type { LanguageModelUsage, ChatStatus } from 'ai';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useChatHistory } from '@/hooks/use-chat-history';
import { Button } from '@/components/ui/button';
import { Menu, Plus, Trash2, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { generateUUID } from '@/lib/utils';
import { MultimodalInput } from './multimodal-input';
import { Messages } from './messages';
import type { VisibilityType } from './visibility-selector';
import { toast } from './toast';
import { useSearchParams } from 'next/navigation';
import { useChatVisibility } from '@/hooks/use-chat-visibility';
import type { Attachment, ChatMessage } from '@/lib/types';
import type {PromptType} from "@/app/(chat)/api/chat/route";

// Vote type definition for stateless mode
type Vote = {
  messageId: string;
  type: 'up' | 'down';
};

// Session type definition for stateless mode
type Session = null;

export function ChatModal({
  id,
  userId,
  companyName,
  userName,
  initialMessages,
  initialChatModel,
  initialVisibilityType,
  isReadonly,
  session,
  autoResume,
  initialLastContext,
  basePath = '/chat',
  initialPromptMode = 'simple',
}: {
  id: string;
  userId?: string | null;
  companyName?: string | null;
  userName?: string | null;
  initialMessages: ChatMessage[];
  initialChatModel: string;
  initialVisibilityType: VisibilityType;
  isReadonly: boolean;
  session: Session;
  autoResume: boolean;
  initialLastContext?: LanguageModelUsage;
  basePath?: string;
  initialPromptMode?: PromptType;
}) {
  const { visibilityType } = useChatVisibility({
    chatId: id,
    initialVisibilityType,
  });

  const { saveChat, getChat, chats, deleteChat } = useChatHistory(
    userId,
    companyName,
    userName,
  );

  const [input, setInput] = useState<string>('');
  const [usage, _setUsage] = useState<LanguageModelUsage | undefined>(
    initialLastContext,
  );
  const [promptMode, setPromptMode] = useState<PromptType>(
    initialPromptMode,
  );
  const [isNavigating, setIsNavigating] = useState(false);

  // Custom chat implementation for plain text streaming
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [status, setStatus] = useState<ChatStatus>('ready');
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  // Chat history sidebar state
  const [showChatHistory, setShowChatHistory] = useState(false);
  const router = useRouter();

  // Référence pour l'auto-scroll
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Auto-scroll behavior: top for single message or initial load, bottom for ongoing conversations
  useEffect(() => {
    if (messagesContainerRef.current) {
      if (isInitialLoad || messages.length === 1) {
        // Scroll to top when first loading a conversation or when there's only 1 message (framework message)
        messagesContainerRef.current.scrollTop = 0;
        setIsInitialLoad(false);
      } else {
        // Scroll to bottom when new messages are added during conversation
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }
  });

  // Load existing chat from database on mount and cleanup empty conversations
  useEffect(() => {
    let isMounted = true;

    const loadExistingChat = async () => {
      // Reset initial load state when switching conversations
      setIsInitialLoad(true);

      try {
        const existingChat = await getChat(id);
        if (isMounted) {
          if (existingChat && existingChat.messages.length > 0) {
            setMessages(existingChat.messages);
            // Load promptMode from existing chat
            if (existingChat.promptMode) {
              setPromptMode(existingChat.promptMode);
            }
          } else {
            // If no existing chat, clear messages for new conversation
            setMessages([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading existing chat:', error);
          setMessages([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingMessages(false);
        }
      }
    };

    loadExistingChat();

    return () => {
      isMounted = false;
    };
  }, [id, getChat]);

  // Debounced save function to prevent excessive API calls
  const debouncedSaveChat = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (
        chatId: string,
        chatMessages: any[],
        mode: PromptType,
      ) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveChat(chatId, chatMessages, mode);
        }, 500); // 500ms debounce
      };
    })(),
    [],
  );

  // Save messages to database with debouncing when there are messages
  useEffect(() => {
    if (messages.length > 0) {
      debouncedSaveChat(id, messages, promptMode);
    }
  }, [messages, id, promptMode, debouncedSaveChat]);

  const sendMessageImpl = useCallback(
    async (message: string | ChatMessage) => {
      setStatus('submitted');

      // Ensure message has proper structure - handle the input message format
      const userMessage: ChatMessage =
        typeof message === 'string'
          ? {
              id: generateUUID(),
              role: 'user',
              parts: [{ type: 'text', text: message }],
              metadata: {
                createdAt: new Date().toISOString(),
              },
            }
          : {
              id: message.id || generateUUID(),
              role: message.role,
              parts: message.parts,
              metadata: message.metadata || {
                createdAt: new Date().toISOString(),
              },
            };

      // Add user message to current conversation
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);

      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: id,
            messages: updatedMessages, // Send full conversation history
            selectedChatModel: 'chat-model',
            selectedVisibilityType: visibilityType,
            promptMode: promptMode,
          }),
        });

        if (!response.ok) throw new Error('Failed to send message');

        // Change to streaming status when we start receiving response
        setStatus('streaming');

        // Create assistant message placeholder when we start streaming
        const assistantMessage: ChatMessage = {
          id: generateUUID(),
          role: 'assistant',
          parts: [{ type: 'text', text: '' }],
          metadata: {
            createdAt: new Date().toISOString(),
          },
        };
        setMessages((prev) => [...prev, assistantMessage]);

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (reader) {
          let content = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            content += chunk;

            // Update the assistant message with streaming content
            setMessages((prev) => {
              const updatedMessages = prev.map((msg) =>
                msg.id === assistantMessage.id
                  ? {
                      ...msg,
                      parts: [{ type: 'text' as const, text: content }],
                    }
                  : msg,
              );
              return updatedMessages;
            });
          }

          if (content.trim().endsWith('Réponds "Continuer" pour la suite.')) {
            setInput('Continuer');
          }
        }
      } catch (error) {
        console.error('Chat error:', error);
        toast({
          type: 'error',
          description: 'Failed to send message',
        });
      } finally {
        setStatus('ready');
      }
    },
    [messages, id, visibilityType, promptMode],
  );

  const stop = () => setStatus('ready');

  const sendMessage = useCallback(
    async (message: any, _options?: any) => {
      // Convert the message parameter to our expected format
      if (typeof message === 'string') {
        await sendMessageImpl(message);
      } else {
        await sendMessageImpl(message as ChatMessage);
      }
    },
    [sendMessageImpl],
  );

  const regenerate = async (_options?: any) => {
    // For stateless chat, we don't implement regeneration
    // This is just to satisfy the type requirements
    console.log('Regenerate not implemented in stateless chat');
  };

  // Auto-display Jordan Khalifa framework message ONLY for complex mode
  useEffect(() => {
    if (
      !isLoadingMessages &&
      messages.length === 0 &&
      promptMode === 'complex'
    ) {
      // Create the AI framework message directly without API call
      const frameworkMessage: ChatMessage = {
        id: generateUUID(),
        role: 'assistant',
        parts: [
          {
            type: 'text',
            text: `
**Qualification – 7 questions:**\n
\n
1. **Qui** est concerné ?\n
2. **Quoi** : quel problème ou objectif ?\n
3. **Où** : lieu, service, marché impacté ?\n
4. **Quand** : échéance ou période critique ?\n
5. **Comment** : méthodes déjà tentées ou envisagées ?\n
6. **Combien** : impact chiffré ou ressources disponibles ?\n
7. **Pourquoi** : raison profonde de cette demande maintenant ?\n
\n
Merci de répondre`,
          },
        ],
        metadata: {
          createdAt: new Date().toISOString(),
        },
      };

      // Small delay to ensure everything is properly initialized
      const timer = setTimeout(() => {
        setMessages([frameworkMessage]);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isLoadingMessages, messages.length, promptMode]);

  const searchParams = useSearchParams();
  const query = searchParams.get('query');
  const context = searchParams.get('context');

  const [hasAppendedQuery, setHasAppendedQuery] = useState(false);
  const [hasAppendedContext, setHasAppendedContext] = useState(false);

  useEffect(() => {
    if (query && !hasAppendedQuery) {
      sendMessage({
        id: generateUUID(),
        role: 'user' as const,
        parts: [{ type: 'text', text: query }],
        metadata: {
          createdAt: new Date().toISOString(),
        },
      });

      setHasAppendedQuery(true);
      const cleanUrl = userId
        ? `${basePath}/${id}?userId=${encodeURIComponent(userId)}`
        : `${basePath}/${id}`;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [query, sendMessage, hasAppendedQuery, id, basePath, userId]);

  // Auto-send context from expert mode button
  useEffect(() => {
    if (context && !hasAppendedContext && !isLoadingMessages) {
      sendMessage({
        id: generateUUID(),
        role: 'user' as const,
        parts: [{ type: 'text', text: context }],
        metadata: {
          createdAt: new Date().toISOString(),
        },
      });

      setHasAppendedContext(true);
      // Clean URL but preserve userId, companyName, userName
      const params = new URLSearchParams();
      if (userId) params.set('userId', userId);
      if (companyName) params.set('companyName', companyName);
      if (userName) params.set('userName', userName);
      const queryString = params.toString();
      const cleanUrl = queryString ? `${basePath}/${id}?${queryString}` : `${basePath}/${id}`;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [context, sendMessage, hasAppendedContext, isLoadingMessages, id, basePath, userId, companyName, userName]);

  // Remove votes functionality for stateless chat
  const votes: Array<Vote> = [];

  const [attachments, setAttachments] = useState<Array<Attachment>>([]);

  const handleNewChat = () => {
    setIsNavigating(true);

    const newChatId = generateUUID();

    // Always start new conversations in simple mode
    const simpleBasePath = basePath.includes('/iframe')
      ? '/iframe/chat'
      : '/chat';

    // Build query parameters to preserve userId, companyName, and userName
    const queryParams = new URLSearchParams();
    if (userId) queryParams.set('userId', userId);
    if (companyName) queryParams.set('companyName', companyName);
    if (userName) queryParams.set('userName', userName);

    const queryString = queryParams.toString();
    const url = queryString
      ? `${simpleBasePath}/${newChatId}?${queryString}`
      : `${simpleBasePath}/${newChatId}`;

    router.push(url);
    setShowChatHistory(false);
  };

  const handleChatSelect = (chatId: string) => {
    // Build query parameters to preserve userId, companyName, and userName
    const queryParams = new URLSearchParams();
    if (userId) queryParams.set('userId', userId);
    if (companyName) queryParams.set('companyName', companyName);
    if (userName) queryParams.set('userName', userName);

    const queryString = queryParams.toString();
    const url = queryString
      ? `${basePath}/${chatId}?${queryString}`
      : `${basePath}/${chatId}`;

    router.push(url);
    setShowChatHistory(false);
  };

  const handleDeleteChat = (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    deleteChat(chatId);

    // If we're deleting the current chat, navigate to a new chat
    if (chatId === id) {
      const newChatId = generateUUID();
      const url = userId
        ? `${basePath}/${newChatId}?userId=${encodeURIComponent(userId)}`
        : `${basePath}/${newChatId}`;
      router.push(url);
    }
  };

  const handleSwitchToComplex = () => {
    // Set loading state immediately for instant feedback
    setIsNavigating(true);

    // Navigate to expert URL (creates new conversation with complex mode)
    const newChatId = generateUUID();

    // Determine the expert base path based on current basePath
    const expertBasePath = basePath.includes('/iframe')
      ? '/iframe/expert'
      : '/expert';

    // Build query parameters to preserve userId, companyName, and userName
    const queryParams = new URLSearchParams();
    if (userId) queryParams.set('userId', userId);
    if (companyName) queryParams.set('companyName', companyName);
    if (userName) queryParams.set('userName', userName);

    const queryString = queryParams.toString();
    const url = queryString
      ? `${expertBasePath}/${newChatId}?${queryString}`
      : `${expertBasePath}/${newChatId}`;

    router.push(url);
    setShowChatHistory(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessageImpl(suggestion);
  };

  return (
    <div className="flex h-full w-full">
      {/* Chat History Sidebar */}
      {showChatHistory && (
        <div className="flex w-80 flex-col border-gray-200 border-r bg-gray-50">
          {/* Sidebar Header */}
          <div className="border-gray-200 border-b p-4">
            <h2 className="font-semibold text-gray-700">
              Historique des conversations
            </h2>
          </div>

          {/* New Chat Button */}
          <div className="border-gray-200 border-b p-4">
            <Button
              onClick={handleNewChat}
              disabled={isNavigating}
              className="w-full justify-start gap-2 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: '#000054' }}
            >
              {isNavigating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Chargement...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Nouvelle conversation
                </>
              )}
            </Button>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto p-4">
            {chats.length === 0 ? (
              <div className="py-8 text-center text-gray-600">
                Aucune conversation
              </div>
            ) : (
              <div className="space-y-1">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => handleChatSelect(chat.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleChatSelect(chat.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className={`group flex cursor-pointer items-center justify-between rounded-lg p-3 transition-colors ${
                      chat.id === id
                        ? 'border border-blue-200 bg-blue-100'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate font-medium text-gray-700 text-sm">
                          {chat.title}
                        </div>
                        {chat.promptMode === 'complex' && (
                          <span
                            className="flex-shrink-0 rounded-full px-2 py-0.5 font-semibold text-white text-xs"
                            style={{ backgroundColor: '#000054' }}
                          >
                            Expert
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-gray-600 text-xs">
                        {new Date(chat.updatedAt).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteChat(chat.id, e)}
                      className="h-8 w-8 p-0 text-gray-600 opacity-0 hover:text-red-600 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col">
        {/* Chat Header with Toggle Button */}
        <div className="flex items-center justify-between border-gray-200 border-b bg-white p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowChatHistory(!showChatHistory)}
            className="gap-2 text-gray-700 hover:bg-gray-100 hover:text-gray-700"
          >
            <Menu className="h-4 w-4" />
            {showChatHistory ? 'Masquer' : 'Conversations'}
          </Button>
        </div>

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto bg-white"
        >
          {/* Informational Message for Empty Chats */}
          {messages.length <= 1 && (
            <div className="flex justify-center p-6">
              <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700 shadow-sm">
                <h2
                  className="mb-2 font-semibold text-lg"
                  style={{ color: '#000054' }}
                >
                  GPT menuiserie
                </h2>
                <p className="text-sm">
                  L'IA de résolution de problèmes entraînée pour la menuiserie
                </p>

                {/* Show full details only in complex mode */}
                {promptMode === 'complex' && (
                  <>
                    <p className="mt-3 mb-3 text-sm">
                      <span className="font-bold underline">Objectif :</span>{' '}
                      identifier rapidement les leviers de croissance et
                      d'efficacité (rentabilité, délais, satisfaction client,
                      acquisition).
                    </p>
                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Étape 1 :</strong> Je vous pose quelques
                        questions ciblées.
                      </p>
                      <p>
                        <strong>Étape 2 :</strong> J'analyse vos réponses et je
                        pose un diagnostic.
                      </p>
                      <p>
                        <strong>Étape 3 :</strong> Je propose un plan d'action
                        priorisé (tactiques commerciales, ajustement des prix,
                        optimisation d'atelier, modèles de documents).
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <Messages
            chatId={id}
            status={status as any}
            votes={votes}
            messages={messages}
            setMessages={setMessages as any}
            regenerate={regenerate as any}
            isReadonly={isReadonly}
            selectedModelId={initialChatModel}
            userId={userId}
            companyName={companyName}
            userName={userName}
            promptMode={promptMode}
            basePath={basePath}
          />

        </div>

        {/* Input Area */}
        <div className="border-gray-200 border-t bg-white p-4">
          {!isReadonly && (
            <div className="mx-auto max-w-4xl">
              {/* Show suggestion buttons only in simple mode with no messages */}
              {promptMode === 'simple' && messages.length === 0 && (
                <>
                  <div className="mb-3 flex flex-col gap-3">
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 p-4 text-left text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:shadow-md"
                      onClick={() =>
                        handleSuggestionClick(
                          'Je voudrais embaucher un commercial, est-ce le bon moment ?',
                        )
                      }
                    >
                      <span className="font-medium text-sm">
                        Je voudrais embaucher un commercial, est-ce le bon
                        moment ?
                      </span>
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 p-4 text-left text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:shadow-md"
                      onClick={() =>
                        handleSuggestionClick(
                          'Ma marge (pose incluse) est inférieure à 32%, et je fais de la fourniture et pose en BtoC, est-ce suffisant ?',
                        )
                      }
                    >
                      <span className="font-medium text-sm">
                        Ma marge (pose incluse) est inférieure à 32%, et je fais
                        de la fourniture et pose en BtoC, est-ce suffisant ?
                      </span>
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 p-4 text-left text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:shadow-md"
                      onClick={() =>
                        handleSuggestionClick(
                          "J'ai le projet d'ouvrir une nouvelle agence, est-ce que mon projet tient la route ?",
                        )
                      }
                    >
                      <span className="font-medium text-sm">
                        J'ai le projet d'ouvrir une nouvelle agence, est-ce que
                        mon projet tient la route ?
                      </span>
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 p-4 text-left text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:shadow-md"
                      onClick={() =>
                        handleSuggestionClick(
                          "Je veux embaucher une nouvelle équipe de poseurs, est-ce adapté à mon niveau d'activité ?",
                        )
                      }
                    >
                      <span className="font-medium text-sm">
                        Je veux embaucher une nouvelle équipe de poseurs, est-ce
                        adapté à mon niveau d'activité ?
                      </span>
                    </button>
                  </div>

                  {/* Mode Expert CTA - Full width */}
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={handleSwitchToComplex}
                      disabled={isNavigating}
                      className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold text-sm text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ backgroundColor: '#000054' }}
                    >
                      {isNavigating ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          Mode Expert
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

              <MultimodalInput
                chatId={id}
                input={input}
                setInput={setInput}
                status={status}
                stop={stop}
                attachments={attachments}
                setAttachments={setAttachments}
                messages={messages}
                setMessages={setMessages}
                sendMessage={sendMessage}
                selectedVisibilityType={visibilityType}
                selectedModelId={initialChatModel}
                usage={usage}
                isLoadingMessages={isLoadingMessages}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
